// src/config/socialLinks.ts
import {
  Facebook,
  Instagram,
  Youtube,
  Twitter,
  Linkedin,
  Phone,
  Mail,
  MapPin,
  MessageCircle,
} from 'lucide-react';
import TikTokIcon from '@/components/icons/TikTokIcon';

/**
 * Social Media Links Configuration
 * Champions Academy Social Media Presence
 */

export interface SocialLink {
  platform: string;
  url: string;
  icon: any;
  label?: {
    en: string;
    ar: string;
  };
  active?: boolean;
}

/**
 * Social Links Array - For mapping in components
 */
export const socialLinks: SocialLink[] = [
  {
    platform: 'Facebook',
    url: '#',
    url: '#',
    icon: Facebook,
    label: {
      en: 'Facebook',
      ar: 'فيسبوك'
    },
    active: true
  },
  {
    platform: 'Instagram',
    url: '#',
    url: '#',
    icon: Instagram,
    label: {
      en: 'Instagram',
      ar: 'انستغرام'
    },
    active: true
  },
  {
    platform: 'YouTube',
    url: '#',
    url: '#',
    icon: Youtube,
    label: {
      en: 'YouTube',
      ar: 'يوتيوب'
    },
    active: true
  },
  {
    platform: 'Twitter',
    url: '#',
    url: '#',
    icon: Twitter,
    label: {
      en: 'Twitter / X',
      ar: 'تويتر / إكس'
    },
    active: true
  },
  {
    platform: 'LinkedIn',
    url: '#',
    url: '#',
    icon: Linkedin,
    label: {
      en: 'LinkedIn',
      ar: 'لينكد إن'
    },
    active: true
  },
  {
    platform: 'TikTok',
    url: '#',
    icon: TikTokIcon,
    label: {
      en: 'TikTok',
      ar: 'تيك توك'
    },
    active: true
  },
  {
    platform: 'WhatsApp',
    url: '#',
    url: '#',
    icon: MessageCircle,
    label: {
      en: 'WhatsApp',
      ar: 'واتساب'
    },
    active: true
  }
];

/**
 * Social Links Object - For direct access by platform name
 * Useful when you need specific platform URL
 */
export const socialLinksMap = {
  facebook: '#',
  instagram: '#',
  youtube: '#',
  twitter: '#',
  linkedin: '#',
  tiktok: '#',
  whatsapp: '#',
  email: 'info@champions.ps',
  phone: '+970 599 000 000'
};

/**
 * Contact Information
 */
export const contactInfo = {
  mainPhone: '+970 599 000 000',
  whatsappNumber: '+970599000000',
  whatsappLink: '#',
  whatsappLink: '#',
  email: 'info@champions.ps',
  supportEmail: 'support@champions.ps',
  mediaEmail: 'media@champions.ps',
  
  mainAddress: {
    en: 'Champions Academy Main Branch, Gaza Strip, Palestine',
    ar: 'الفرع الرئيسي لأكاديمية تشامبيونز، قطاع غزة، فلسطين'
  },
  
  coordinates: {
    lat: 31.5017,
    lng: 34.4668
  },
  
  workingHours: {
    weekdays: {
      en: 'Saturday - Thursday: 8:00 AM - 10:00 PM',
      ar: 'السبت - الخميس: 8:00 صباحاً - 10:00 مساءً'
    },
    friday: {
      en: 'Friday: 2:00 PM - 10:00 PM',
      ar: 'الجمعة: 2:00 ظهراً - 10:00 مساءً'
    }
  }
};

/**
 * Emergency Contact
 */
export const emergencyContact = {
  phone: '+970 59 999 9999',
  whatsapp: '#',
  available247: true,
  description: {
    en: '24/7 Emergency Hotline',
    ar: 'خط طوارئ 24/7'
  }
};

/**
 * Department Emails
 */
export const departmentEmails = {
  general: 'info@champions.ps',
  admissions: 'admissions@champions.ps',
  coaching: 'coaching@champions.ps',
  media: 'media@champions.ps',
  partnerships: 'partnerships@champions.ps',
  support: 'support@champions.ps',
  hr: 'hr@champions.ps'
};

/**
 * Branch Contact Numbers
 */
export const branchContacts = {
  main: '+970 59 123 4567',
  jabalia: '+970 59 123 4568',
  khanyounis: '+970 59 123 4569',
  rafah: '+970 59 123 4570',
  deirbalah: '+970 59 123 4571',
  beach: '+970 59 123 4572',
  beithanoun: '+970 59 123 4573'
};

/**
 * Social Media Stats (Optional - for display purposes)
 */
export const socialStats = {
  facebook: {
    followers: '50K+',
    platform: 'Facebook'
  },
  instagram: {
    followers: '35K+',
    platform: 'Instagram'
  },
  youtube: {
    subscribers: '20K+',
    platform: 'YouTube'
  },
  twitter: {
    followers: '15K+',
    platform: 'Twitter/X'
  }
};

/**
 * Helper function to get active social links only
 */
export const getActiveSocialLinks = () => {
  return socialLinks.filter(link => link.active !== false);
};

/**
 * Helper function to get social link by platform
 */
export const getSocialLinkByPlatform = (platform: string): SocialLink | undefined => {
  return socialLinks.find(
    link => link.platform.toLowerCase() === platform.toLowerCase()
  );
};

// Export default for convenience
export default {
  socialLinks,
  socialLinksMap,
  contactInfo,
  emergencyContact,
  departmentEmails,
  branchContacts,
  socialStats,
  getActiveSocialLinks,
  getSocialLinkByPlatform
};
